import type { Exercise } from "services/exercises/model";
import type { LibraryObject } from "services/content/model";
import { api } from "services/restUtil";
import { apiQuery } from "utils/queries";
import { infiniteApiQuery } from "utils/queries/infiniteQueries";
import type { UserTag } from "services/users/model";

type TagPageParams = {
  page?: number;
  size?: number;
};

type FetchLibraryByTagParams = TagPageParams & {
  id: string;
};

type FetchLibraryByTagResponse = {
  tag: UserTag;
  list: LibraryObject[];
  total: number;
};

// Both /tag/:id/* endpoints answer with `total`, where the infinite factory expects
// `total_count`.
export const libraryByTagQuery = infiniteApiQuery<LibraryObject, { id: string }>({
  path: "/tag/:id/library",
  queryFn: (_path, params) =>
    fetchLibraryByTag(params).then(resp => ({
      list: resp.list,
      total_count: resp.total,
    })),
});

export const exercisesByTagQuery = infiniteApiQuery<Exercise, { id: string }>({
  path: "/tag/:id/exercises",
  queryFn: (_path, params) =>
    fetchExercisesByTag(params).then(resp => ({
      list: resp.list,
      total_count: resp.total,
    })),
});

function fetchLibraryByTag(args: FetchLibraryByTagParams) {
  return api.get<FetchLibraryByTagResponse>(`/tag/${args.id}/library`, {
    page: args.page,
    size: args.size,
  });
}

type FetchExercisesByTagParams = TagPageParams & {
  id: string;
};

type FetchExercisesByTagResponse = {
  tag: UserTag;
  list: Exercise[];
  total: number;
};

export function fetchExercisesByTag(args: FetchExercisesByTagParams) {
  return api.get<FetchExercisesByTagResponse>(`/tag/${args.id}/exercises`, {
    page: args.page,
    size: args.size,
  });
}

type FetchTagsParams = TagPageParams & {
  name?: string;
  category?: string;
};

type FetchTagsResponse = {
  list: UserTag[];
  total_count: number;
};

export function fetchTags(args?: FetchTagsParams) {
  return api.get<FetchTagsResponse>("/tags", args);
}

export const tagsQuery = apiQuery<FetchTagsResponse, FetchTagsParams | void>({
  key: "/tags",
});

export function fetchTagsByCategory(category: string) {
  return (args: FetchTagsByCategoryParams = {}) => {
    return fetchTags({
      ...args,
      category,
    });
  };
}

type FetchTagsByCategoryParams = Omit<FetchTagsParams, "category">;

type AddTagToContentPayload = {
  id: string;
  tags: string[];
};

export function addTagToContent(args: AddTagToContentPayload) {
  return api.post<AddTagToContentPayload, void>("/tag/content", args);
}

type DeleteTagFromContentPayload = AddTagToContentPayload;

export function deleteTagFromContent(args: DeleteTagFromContentPayload) {
  return api.delete<DeleteTagFromContentPayload, void>("/tag/content", args);
}

export * from "./utils";
